import { ChangeEvent, FormEvent, useState } from 'react';

import Button from '@common/Button/Button';
import { BodyRegular } from '@common/body';

import { ErrorTracking } from '@common/errorTracking';
import { getAPIUrl } from '@nextUtils/urlUtils';

const reasonsForContact = [
  'General enquiry',
  'New business',
  'Press enquiry',
  'Technical support',
] as const;

type formState = {
  fullName: string;
  email: string;
  question: string;
  reasonForContact: (typeof reasonsForContact)[number];
};

const initialState: formState = {
  fullName: '',
  email: '',
  question: '',
  reasonForContact: 'General enquiry',
};

type Props = {
  includeNameField: boolean;
  messagePlaceholder: string;
};

const ContactForm = ({
  includeNameField,
  messagePlaceholder,
}: Props): JSX.Element => {
  const [formData, setFormData] = useState<formState>(initialState);

  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showFailure, setShowFailure] = useState(false);
  const [showBruteforce, setShowBruteforce] = useState(false);

  const onChange = (
    event: ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) =>
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmittingForm) {
      return;
    }

    setIsSubmittingForm(true);
    setShowSuccess(false);
    setShowFailure(false);
    setShowBruteforce(false);

    try {
      const response = await fetch(`${getAPIUrl()}/api/ask-question`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        setShowSuccess(true);
        setFormData(initialState);
      } else if (response.status === 429) {
        setShowBruteforce(true);
      } else {
        setShowFailure(true);
      }
    } catch (err) {
      ErrorTracking.track(err);
      setShowFailure(true);
    } finally {
      setIsSubmittingForm(false);
    }
  };

  return (
    <form
      className="flex w-full flex-col items-start lg:w-auto"
      onSubmit={onSubmit}
    >
      {includeNameField && (
        <input
          name="fullName"
          placeholder="Full name"
          type="text"
          value={formData.fullName}
          onChange={onChange}
          required
          className="mb-4 w-full rounded-lg bg-mono-white p-4 text-blue placeholder:text-blue-600 lg:w-[424px]"
        />
      )}
      <input
        name="email"
        placeholder="Email"
        type="email"
        value={formData.email}
        onChange={onChange}
        required
        className="mb-4 w-full rounded-lg bg-mono-white p-4 text-blue placeholder:text-blue-600 lg:w-[424px]"
      />
      <select
        name="reasonForContact"
        onChange={onChange}
        className="mb-4 w-full rounded-lg border-r-4 bg-mono-white p-4 text-blue placeholder:text-blue-600 lg:w-[424px]"
      >
        {reasonsForContact.map(reason => (
          <option key={reason} value={reason}>
            {reason}
          </option>
        ))}
      </select>
      <textarea
        name="question"
        placeholder={messagePlaceholder}
        value={formData.question}
        onChange={onChange}
        required
        className="mb-6 h-[144px] w-full resize-y rounded-lg bg-mono-white p-4 text-blue placeholder:text-blue-600 lg:w-[424px]"
      />
      <div className="w-full lg:w-auto">
        <Button type="Primary" colour="NavyBlue" buttonType="submit" fullWidth>
          Send message
        </Button>
        {showSuccess && (
          <div className="mt-3 text-system-success">
            <BodyRegular>
              Message sent! We{"'"}ll get back to you ASAP.
            </BodyRegular>
          </div>
        )}
        {showFailure && (
          <div className="mt-3 text-system-error">
            <BodyRegular>
              There was an error submitting your comment.
            </BodyRegular>
          </div>
        )}
        {showBruteforce && (
          <div className="mt-3 text-system-error">
            <BodyRegular>You have submitted too many comments.</BodyRegular>
          </div>
        )}
      </div>
    </form>
  );
};

ContactForm.defaultProps = {
  includeNameField: true,
  messagePlaceholder: 'Message',
};

export default ContactForm;
